(() => {
  if (customElements.get('related-products-sidebar')) return;

  class RelatedProductsSidebar extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal, passive: true };
      this.initSlider(options);

      this.main = this.closest('#main_content');
      if (!this.main) return;
      this.sectionWrapper = this.closest('.section--related-products-sidebar');
      this.panel = this.querySelector('.related-sidebar--panel');

      this.media = window.matchMedia('(min-width: 1024px)');
      this.frame = 0;
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };
      this.refreshChrome = () => {
        this.chrome = [...document.querySelectorAll('.header--root, .announcement--root, .split-announcement--root')];
        this.observer?.disconnect();
        this.observer?.observe(this.main);
        if (this.panel) this.observer?.observe(this.panel);
        this.chrome.forEach(element => this.observer?.observe(element));
        this.schedule();
      };

      window.addEventListener('scroll', this.schedule, options);
      window.addEventListener('resize', this.schedule, options);
      this.media.addEventListener('change', this.schedule, options);
      // Header hide/reveal transitions and editor replacements can change offsets.
      document.addEventListener('transitionend', this.schedule, options);
      document.addEventListener('shopify:section:load', this.refreshChrome, options);
      document.addEventListener('shopify:section:unload', this.refreshChrome, options);
      if ('ResizeObserver' in window) this.observer = new ResizeObserver(this.schedule);
      this.refreshChrome();
    }

    // Mobile and tablet product slider arrows, matching Article Suggestions.
    initSlider(options) {
      const grid = this.querySelector('.related-sidebar--slider .related-products--grid');
      if (!grid) return;
      const prev = this.querySelector('.related-sidebar--slider .related-prev');
      const next = this.querySelector('.related-sidebar--slider .related-next');

      const updateButtons = () => {
        const maxScroll = grid.scrollWidth - grid.clientWidth;
        const atStart = grid.scrollLeft <= 0;
        const atEnd = grid.scrollLeft >= maxScroll - 1;
        prev?.setAttribute('aria-disabled', atStart);
        next?.setAttribute('aria-disabled', atEnd);
      };
      const scrollByCards = direction => {
        const item = grid.querySelector('[data-product-item]');
        const amount = item ? 2 * (item.offsetWidth + 16) : 300;
        this.smoothScrollTo(grid, grid.scrollLeft + direction * amount, 600);
      };

      prev?.addEventListener('click', () => scrollByCards(-1), { signal: options.signal });
      next?.addEventListener('click', () => scrollByCards(1), { signal: options.signal });
      grid.addEventListener('scroll', updateButtons, options);
      window.addEventListener('resize', updateButtons, options);
      updateButtons();
    }

    smoothScrollTo(element, target, duration) {
      const start = element.scrollLeft;
      const change = target - start;
      const startTime = performance.now();
      const easeInOut = t => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
      const animate = time => {
        const progress = Math.min((time - startTime) / duration, 1);
        element.scrollLeft = start + change * easeInOut(progress);
        if (progress < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }

    update() {
      if (!this.isConnected) return;
      if (!this.panel) {
        this.panel = this.querySelector('.related-sidebar--panel');
        if (!this.panel) return;
        this.observer?.observe(this.panel);
      }
      if (!this.media.matches || this.dataset.sidebarEnabled !== 'true') {
        this.releaseLayout();
        return;
      }
      // The section is limited to one instance; this also protects editor swaps.
      if (this.main.relatedSidebarOwner && this.main.relatedSidebarOwner !== this) return;
      this.main.relatedSidebarOwner = this;
      const configuredWidth = Number(this.dataset.sidebarWidth) || 320;
      const width = Math.min(configuredWidth, document.documentElement.clientWidth * 0.32);
      this.setStyle(this.main, '--related-sidebar-reserved-width', `${width}px`);
      this.main.setAttribute('data-related-sidebar-active', '');
      this.sectionWrapper?.setAttribute('data-sidebar-host-active', '');
      this.setAttribute('data-sidebar-active', '');

      let headerBottom = 0;
      for (const element of this.chrome) {
        if (element.getAttribute('aria-hidden') === 'true') continue;
        const rect = element.getBoundingClientRect();
        if (rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight) {
          headerBottom = Math.max(headerBottom, rect.bottom);
        }
      }
      const gap = Number(this.dataset.topGap) || 0;
      const panelHeight = Math.ceil(this.panel.getBoundingClientRect().height);
      // A negative sticky top lets a tall panel scroll with the document until
      // its bottom meets the viewport. Short panels stay below the header.
      const stickyTop = Math.min(headerBottom + gap, window.innerHeight - panelHeight);
      this.setStyle(this, '--related-sidebar-sticky-top', `${Math.round(stickyTop)}px`);
      this.setStyle(this, '--related-sidebar-top-gap', `${gap}px`);
      // Give the rail enough space on short pages. Native sticky positioning
      // then stops at main's bottom without covering the footer.
      this.setStyle(this.main, '--related-sidebar-min-height', `${panelHeight + gap}px`);
    }

    setStyle(element, property, value) {
      if (element.style.getPropertyValue(property) !== value) {
        element.style.setProperty(property, value);
      }
    }

    releaseLayout() {
      if (this.main?.relatedSidebarOwner === this) {
        this.main.removeAttribute('data-related-sidebar-active');
        this.main.style.removeProperty('--related-sidebar-reserved-width');
        this.main.style.removeProperty('--related-sidebar-min-height');
        delete this.main.relatedSidebarOwner;
      }
      this.removeAttribute('data-sidebar-active');
      this.sectionWrapper?.removeAttribute('data-sidebar-host-active');
      this.style.removeProperty('--related-sidebar-sticky-top');
      this.style.removeProperty('--related-sidebar-top-gap');
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.releaseLayout();
    }
  }

  customElements.define('related-products-sidebar', RelatedProductsSidebar);
})();
